
import { defineComponent, onBeforeMount, reactive } from 'vue'

// module components
import ComplaintForm from '@/modules/PretensionsModule/components/ComplaintForm/index.vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// types
import { Complaint } from '@/new-services/types/complaints'
import { EmployeeT, UserDictionaryT, UserT } from '@/core/types/users.types'
import { CompanyT } from '@/core/types/companies.types'

// @new-services
import complaintsAPI from '@/new-services/complaints.service'

// realisations
import { users } from '@/core/realisations/users.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// constants
import { ComplaintsColumns, ComplaintsIdColumns } from '@/core/constants/columns/Complaints.columns'
import {
    COMPLAINT_STATUSES,
    COMPLAINT_TYPES,
    COMPLAINT_TYPE_OPTIONS,
} from '@/core/constants/complaints.constants'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useFile } from '@/common/composable/useFile'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
        FailedFetch,
        TheTable,
        TableScrollWrapper,
        ComplaintForm,
    },
    setup() {
        const notifications = useNotifications()
        const { saveFile } = useFile()

        const state = reactive({
            complaints: new DataState<Complaint>({}),
            departments: new DataState<UserDictionaryT>({}),
            employees: new DataState<EmployeeT>({}),
            users: new DataState<UserT>({}),
            companies: new DataState<CompanyT>({}),
            //
            is_loading: false,
            is_excel_loading: false,
        })

        const form_state = reactive({
            is_opened: false,
            complaint_id: null as number | null,
        })

        const filters_state = reactive({
            limit: 50,
            //
            typeId: null as number | null,
            statusId: null as number | null,
            culpritSideId: null as number | null,
            fromDate: null as string | null,
            toDate: null as string | null,
            id: null as string | null,
            invoiceNumber: null as string | null,
            initiator: null as string | null,
            accusedEmployeeId: null as number | null,
            initiatorId: null as number | null,
            companyId: null as number | null,
            categoryId: null as number | null,
        })

        const exportExcel = async () => {
            try {
                state.is_excel_loading = true

                const response = await complaintsAPI.exportExcel()

                saveFile(response.data, 'Жалобы')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                state.is_excel_loading = false
            }
        }

        const loadComplaints = async (page: number) => {
            state.complaints.pagination.current_page = page

            try {
                state.complaints.is_loading = true

                const response = await complaintsAPI.getList({ page, ...filters_state })

                state.complaints.list = response.data.data
                state.complaints.pagination.total_pages = response.data.meta.last_page
                state.complaints.pagination.total_items = response.data.meta.total

                state.complaints.is_failed = false
                state.complaints.failure.error_state.is_cancel = false
            } catch (error) {
                state.complaints.failure.catchError(error)

                if (!state.complaints.failure.error_state.is_cancel) {
                    state.complaints.is_failed = true
                }
            } finally {
                if (!state.complaints.failure.error_state.is_cancel) {
                    state.complaints.is_loading = false
                }
            }
        }

        const resetFilters = () => {
            filters_state.typeId = null
            filters_state.statusId = null
            filters_state.culpritSideId = null
            filters_state.fromDate = null
            filters_state.toDate = null
            filters_state.id = null
            filters_state.invoiceNumber = null
            filters_state.initiator = null
            filters_state.accusedEmployeeId = null
            filters_state.initiatorId = null
            filters_state.companyId = null
            filters_state.categoryId = null

            loadComplaints(1)
        }

        const loadDictionary = async (type: number) => {
            try {
                state.departments.is_loading = true

                const response = await users.getDictionary(type)
                state.departments.list = response.data

                state.departments.is_failed = false
                state.departments.failure.error_state.is_cancel = false
            } catch (error) {
                state.departments.failure.catchError(error)

                if (!state.departments.failure.error_state.is_cancel) {
                    state.departments.is_failed = true
                }
            } finally {
                if (!state.departments.failure.error_state.is_cancel) {
                    state.departments.is_loading = false
                }
            }
        }

        const onUpdateComplaint = (complaint: Complaint) => {
            form_state.complaint_id = complaint.id
            form_state.is_opened = true
        }

        const onAddComplaint = () => {
            form_state.complaint_id = null
            form_state.is_opened = true
        }

        const closeComplaintForm = () => {
            form_state.is_opened = false
            form_state.complaint_id = null
            loadComplaints(state.complaints.pagination.current_page)
        }

        // сотрудники

        const loadEmployees = async (page: number, search?: string) => {
            state.employees.pagination.current_page = page

            try {
                state.employees.is_loading = true

                const params = {
                    page,
                    search,
                }

                const response = await users.getEmployees(params)

                state.employees.list = response.data

                state.employees.pagination.total_pages = response.meta.last_page

                state.employees.is_failed = false
                state.employees.failure.error_state.is_cancel = false
            } catch (error) {
                state.employees.failure.catchError(error)

                if (!state.employees.failure.error_state.is_cancel) {
                    state.employees.is_failed = true
                }
            } finally {
                if (!state.employees.failure.error_state.is_cancel) {
                    state.employees.is_loading = false
                }
            }
        }

        const loadMoreEmployees = async (page: number) => {
            if (page > state.employees.pagination.total_pages) return
            state.employees.pagination.current_page = page

            try {
                state.employees.is_loading = true

                const params = {
                    page,
                }

                const response = await users.getEmployees(params)
                state.employees.list = state.employees.list.concat(response.data)

                state.employees.pagination.total_pages = response.meta.last_page

                state.employees.is_failed = false
                state.employees.failure.error_state.is_cancel = false
            } catch (error) {
                state.employees.failure.catchError(error)

                if (!state.employees.failure.error_state.is_cancel) {
                    state.employees.is_failed = true
                }
            } finally {
                if (!state.employees.failure.error_state.is_cancel) {
                    state.employees.is_loading = false
                }
            }
        }

        const searchEmployees = (search_query: string) => {
            loadEmployees(1, search_query)
        }

        const unshiftFindedEmployee = async (employee: EmployeeT) => {
            await loadEmployees(state.employees.pagination.current_page)
            state.employees.list.unshift(employee)
        }

        // пользователи

        const loadUsers = async (page: number, search?: string) => {
            state.users.pagination.current_page = page

            try {
                state.users.is_loading = true

                const params = { page, name: search }

                const response = await users.getList(params)

                state.users.list = response.data

                state.users.pagination.total_pages = response.meta.last_page

                state.users.is_failed = false
                state.users.failure.error_state.is_cancel = false
            } catch (error) {
                state.users.failure.catchError(error)

                if (!state.users.failure.error_state.is_cancel) {
                    state.users.is_failed = true
                }
            } finally {
                if (!state.users.failure.error_state.is_cancel) {
                    state.users.is_loading = false
                }
            }
        }

        const loadMoreUsers = async (page: number) => {
            if (page > state.users.pagination.total_pages) return
            state.users.pagination.current_page = page

            try {
                state.users.is_loading = true

                const params = {
                    page,
                }

                const response = await users.getList(params)
                state.users.list = state.users.list.concat(response.data)

                state.users.pagination.total_pages = response.meta.last_page

                state.users.is_failed = false
                state.users.failure.error_state.is_cancel = false
            } catch (error) {
                state.users.failure.catchError(error)

                if (!state.users.failure.error_state.is_cancel) {
                    state.users.is_failed = true
                }
            } finally {
                if (!state.users.failure.error_state.is_cancel) {
                    state.users.is_loading = false
                }
            }
        }

        const searchUsers = (search_query: string) => {
            loadUsers(1, search_query)
        }

        //

        const loadCompanies = async (page: number, search?: string) => {
            state.companies.pagination.current_page = page

            try {
                state.companies.is_loading = true

                const params = {
                    page,
                    search,
                    limit: 9999,
                    isExtended: 0,
                    getAll: 1,
                }
                const response = await companies.getList(params)
                state.companies.list = response.data

                state.companies.pagination.total_pages = response.meta.last_page

                state.companies.is_failed = false
                state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                state.companies.failure.catchError(error)

                if (!state.companies.failure.error_state.is_cancel) {
                    state.companies.is_failed = true
                }
            } finally {
                if (!state.companies.failure.error_state.is_cancel) {
                    state.companies.is_loading = false
                }
            }
        }

        //

        const unshiftFindedUser = async (user: UserT) => {
            await loadUsers(state.users.pagination.current_page)
            state.users.list.unshift(user)
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadComplaints(state.complaints.pagination.current_page)
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            return dateFirst.split('-').reverse().join('.')
        }

        //

        onBeforeMount(() => {
            loadComplaints(1)
            loadDictionary(1)
            loadEmployees(1)
            loadUsers(1)
            loadCompanies(1)
        })

        //

        return {
            state,
            filters_state,
            form_state,
            //
            resetFilters,
            onLimitChange,
            //
            loadComplaints,
            loadDictionary,
            onUpdateComplaint,
            onAddComplaint,
            closeComplaintForm,
            exportExcel,
            //
            loadEmployees,
            loadMoreEmployees,
            searchEmployees,
            unshiftFindedEmployee,
            //
            loadUsers,
            loadMoreUsers,
            searchUsers,
            unshiftFindedUser,
            //
            loadCompanies,
            formatDate,
            //
            ComplaintsColumns,
            ComplaintsIdColumns,
            COMPLAINT_STATUSES,
            COMPLAINT_TYPES,
            COMPLAINT_TYPE_OPTIONS,
        }
    },
})
